import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'

export type ChipVariantProps = VariantProps<typeof chipVariants>
export type ChipIntent = NonNullable<ChipVariantProps['intent']>
export type ChipSize = NonNullable<ChipVariantProps['size']>
export type ChipColor = NonNullable<ChipVariantProps['color']>
export type ChipInteractive = NonNullable<ChipVariantProps['interactive']>

export const chipVariants = cva([
  'flex',
  'items-center',
  'justify-center',
  'text-blurple-500',
  'w-fit',
  'outline',
  'outline-offset-2',
  'disabled:opacity-50',
], {
  variants: {
    intent: {
      filled: [
        'border',
        'border-transparent',
      ],
      outlined: [
        'border',
      ],
      neutral: [
        'bg-inherit',
        'text-inherit',
      ],
    },
    size: {
      small: [
        'text-caption',
        'py-[0.3125rem]',
        'px-2xs',
        'gap-2xs',
        'rounded',
        '[&>svg]:!size-4',
      ],
      medium: [
        'text-xsmall',
        'py-[0.3125rem]',
        'px-2xs',
        'gap-2xs',
        'rounded',
        '[&>svg]:!size-5',
      ],
      large: [
        'text-small',
        'py-2.5xs',
        'px-s',
        'gap-2xs',
        'rounded-lg',
        '[&>svg]:!size-5',
      ],
    },
    color: {
      blurple: [],
      moss: [],
      carbon: ['text-carbon-500'],
    },
    interactive: {
      true: [],
      false: [],
    },
  },
  compoundVariants: [
    // Non-hover compounds
    {
      intent: 'filled',
      color: 'blurple',
      size: ['small', 'large', 'medium'],
      class: [
        'text-inverse',
        'bg-surface-invert',
      ],
    },
    {
      intent: 'outlined',
      color: 'blurple',
      size: ['small', 'large', 'medium'],
      class: [
        'border-border-default',
        'focus:outline-blurple-200',
      ],
    },
    {
      intent: 'filled',
      color: 'blurple',
      size: ['small', 'large', 'medium'],
      class: [
        'focus:outline-blurple-200',
      ],
    },
    {
      intent: 'outlined',
      color: 'moss',
      size: ['small', 'large', 'medium'],
      class: [
        'border-border-default',
        'focus:outline-moss-200',
      ],
    },
    {
      intent: 'filled',
      color: 'moss',
      size: ['small', 'large', 'medium'],
      class: [
        'focus:outline-moss-200',
      ],
    },
    {
      intent: 'filled',
      color: 'blurple',
      class: [
        'bg-blurple-50',
      ],
    },
    {
      intent: 'filled',
      color: 'moss',
      class: [
        'text-moss-500',
        'bg-moss-200',
      ],
    },
    {
      intent: 'filled',
      color: 'carbon',
      class: [
        'text-carbon-500',
        'bg-carbon-100',
      ],
    },
    {
      intent: 'outlined',
      color: 'moss',
      size: ['small', 'medium'],
      class: [
        'text-moss-200',
        'border-moss-200',
      ],
    },
    {
      intent: 'outlined',
      color: 'carbon',
      class: [
        'text-carbon-500',
        'border-border-default',
        'dark:border-carbon-50',
        'dark:text-carbon-50',
        'dark:hover:bg-carbon-400',
        'dark:hover:border-carbon-400',
      ],
    },
    {
      intent: 'neutral',
      color: 'carbon',
      class: [
        'text-carbon-500',
        'bg-inherit',
        'focus:outline-carbon-200',
        'reading-dark:text-carbon-50',
      ],
    },
    {
      intent: 'neutral',
      color: 'blurple',
      class: [
        'text-blurple-500',
        'bg-inherit',
        'focus:outline-blurple-200',
      ],
    },
    {
      intent: 'neutral',
      color: 'moss',
      class: [
        'text-moss-500',
        'bg-inherit',
        'focus:outline-moss-200',
      ],
    },

    // Hover-related compounds
    {
      intent: 'filled',
      interactive: true,
      class: [
        'hover:border-2',
      ],
    },
    {
      intent: ['outlined', 'filled'],
      color: 'blurple',
      size: ['small', 'large', 'medium'],
      interactive: true,
      class: [
        'hover:border-2',
        'hover:border-border-primary',
      ],
    },
    {
      intent: 'filled',
      color: ['blurple', 'moss', 'carbon'],
      size: ['small', 'medium'],
      interactive: true,
      class: [
        'hover:px-[calc(theme(spacing.2xs)-1px)]',
        'hover:py-[calc(5px-1px)]',
      ],
    },
    {
      intent: 'outlined',
      color: ['blurple', 'moss'],
      size: ['small', 'medium'],
      interactive: true,
      class: [
        'hover:px-[calc(theme(spacing.2xs)-1px)]',
        'hover:py-[calc(5px-1px)]',
      ],
    },
    {
      intent: ['outlined', 'filled'],
      color: 'blurple',
      size: 'large',
      interactive: true,
      class: [
        'hover:py-[calc(10px-1px)]',
        'hover:px-[calc(theme(spacing.s)-1px)]',
      ],
    },
    {
      intent: ['outlined', 'filled'],
      color: 'moss',
      size: ['small', 'large', 'medium'],
      interactive: true,
      class: [
        'hover:border-moss-200',
      ],
    },
    {
      intent: 'filled',
      color: 'carbon',
      interactive: true,
      class: [
        'hover:border-carbon-500',
      ],
    },
  ],
})
